import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navbar, Nav, Container, Offcanvas, Badge, Image, Button } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { logout } from '../features/users/userSlice';
import { fetchChats } from '../features/chat/chatSlice';
import { useNavigate } from 'react-router-dom';
import SearchBox from './SearchBox';
import { FaUser, FaHeart, FaEnvelope, FaBars, FaSun, FaMoon, FaMapMarkerAlt, FaBoxOpen } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { fixImageUrl } from '../utils/imageUtils';
import { ThemeContext } from '../context/ThemeContext';

const Header = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userInfo } = useSelector((state) => state.user);
    const { chats } = useSelector((state) => state.chat);
    const { theme, toggleTheme } = useContext(ThemeContext);

    const [showMenu, setShowMenu] = useState(false);

    const handleClose = () => setShowMenu(false);
    const handleShow = () => setShowMenu(true);

    useEffect(() => {
        if (userInfo) {
            dispatch(fetchChats());
        }
    }, [dispatch, userInfo]);

    const logoutHandler = () => {
        dispatch(logout());
        navigate('/login');
        handleClose();
    };

    const unreadCount = (userInfo && chats) ? chats.reduce((acc, chat) => {
        return acc + (chat.unreadCounts && chat.unreadCounts[userInfo._id] ? chat.unreadCounts[userInfo._id] : 0);
    }, 0) : 0;

    return (
        <header className="sticky-top" style={{ zIndex: 1000 }}>
            <Navbar
                expand="false"
                className="classic-navbar py-2 m-0 p-0"
                variant="light"
                style={{ borderRadius: '0 0 24px 24px' }}
            >
                <Container fluid className="d-flex flex-column px-4">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        {/* Left: Logo */}
                        <div className="d-flex align-items-center gap-3">
                            <LinkContainer to="/">
                                <Navbar.Brand className="d-flex align-items-center">
                                    <motion.div
                                        whileHover={{ rotate: 360 }}
                                        transition={{ duration: 1 }}
                                        style={{
                                            padding: '3px',
                                            background: 'linear-gradient(45deg, var(--deep-green), var(--primary-gold))',
                                            borderRadius: '50%'
                                        }}
                                    >
                                        <Image
                                            src="/images/logo.jpg"
                                            alt="FruValley"
                                            roundedCircle
                                            style={{ height: '55px', width: '55px', objectFit: 'cover', border: '2px solid white' }}
                                        />
                                    </motion.div>
                                    <div className="ms-3 d-flex flex-column">
                                        <span style={{ fontFamily: 'Playfair Display', fontWeight: '800', color: 'var(--deep-green)', fontSize: '1.8rem', lineHeight: '1' }}>
                                            FruValley Store
                                        </span>
                                        <span style={{ fontFamily: 'Inter', fontSize: '0.75rem', color: 'var(--primary-gold)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                                            Essence of Kashmir
                                        </span>
                                    </div>
                                </Navbar.Brand>
                            </LinkContainer>
                        </div>

                        {/* Center: Search Box (Desktop Only) */}
                        <div className="d-none d-lg-block mx-4" style={{ maxWidth: '300px' }}>
                            <SearchBox />
                        </div>

                        {/* Center-Right: Nav Links (Desktop) */}
                        <Nav className="d-none d-lg-flex flex-row align-items-center gap-3 mx-auto">
                            <LinkContainer to="/">
                                <Nav.Link className="text-deep-green fw-bold" style={{ fontFamily: 'Inter', fontSize: '1rem' }}>Home</Nav.Link>
                            </LinkContainer>
                            <LinkContainer to="/shop">
                                <Nav.Link className="text-deep-green fw-bold" style={{ fontFamily: 'Inter', fontSize: '1rem' }}>Shop</Nav.Link>
                            </LinkContainer>
                            <LinkContainer to="/location">
                                <Nav.Link className="text-deep-green fw-bold" style={{ fontFamily: 'Inter', fontSize: '1rem' }}>Location</Nav.Link>
                            </LinkContainer>
                            <LinkContainer to="/blog">
                                <Nav.Link className="text-deep-green fw-bold" style={{ fontFamily: 'Inter', fontSize: '1rem' }}>Blogs</Nav.Link>
                            </LinkContainer>
                        </Nav>

                        {/* Right: Icons & Menu */}
                        <div className="d-flex align-items-center gap-2">
                            {userInfo && (
                                <LinkContainer to="/message-center">
                                    <Nav.Link className="position-relative text-deep-green p-1">
                                        <FaEnvelope size={22} />
                                        {unreadCount > 0 && (
                                            <Badge
                                                pill
                                                bg="danger"
                                                className="position-absolute top-0 start-100 translate-middle"
                                                style={{ fontSize: '0.6rem' }}
                                            >
                                                {unreadCount}
                                            </Badge>
                                        )}
                                    </Nav.Link>
                                </LinkContainer>
                            )}

                            {userInfo ? (
                                <>
                                    <LinkContainer to="/seller/productlist" className="d-none d-md-block">
                                        <Nav.Link className="text-deep-green p-1">
                                            <FaBoxOpen size={22} />
                                        </Nav.Link>
                                    </LinkContainer>

                                    <LinkContainer to="/myspots" className="d-none d-md-block">
                                        <Nav.Link className="text-deep-green p-1">
                                            <FaMapMarkerAlt size={22} />
                                        </Nav.Link>
                                    </LinkContainer>

                                    <LinkContainer to="/wishlist" className="d-none d-md-block">
                                        <Nav.Link className="text-deep-green p-1">
                                            <FaHeart size={22} />
                                        </Nav.Link>
                                    </LinkContainer>

                                    <LinkContainer to={`/profile/${userInfo._id}`}>
                                        <Nav.Link className="p-0 ms-2">
                                            <Image
                                                src={fixImageUrl(userInfo.profilePicture) || 'https://via.placeholder.com/150'}
                                                roundedCircle
                                                style={{ width: '40px', height: '40px', objectFit: 'cover', border: '2px solid var(--primary-gold)' }}
                                            />
                                        </Nav.Link>
                                    </LinkContainer>
                                </>
                            ) : (
                                <LinkContainer to="/login">
                                    <Nav.Link className="fw-bold text-deep-green">Login</Nav.Link>
                                </LinkContainer>
                            )}

                            {/* Mobile Menu Button - Moved to Right */}
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={handleShow}
                                className="btn btn-link text-deep-green p-0 ms-2"
                            >
                                <FaBars size={28} />
                            </motion.button>
                        </div>
                    </div>

                    {/* Mobile Search Icon - Row Below Logo */}
                    <div className="d-flex d-lg-none mt-2">
                        {/* Using SearchBox which has the icon toggle functionality */}
                        <div className="d-flex align-items-center w-100">
                            <SearchBox alwaysOpen={true} />
                        </div>
                    </div>
                </Container>
            </Navbar>

            <Offcanvas show={showMenu} onHide={handleClose} placement="end" className="glass-panel border-0">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="fw-bold text-deep-green" style={{ fontFamily: 'Playfair Display' }}>Menu</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="d-lg-none mb-4">
                        <SearchBox />
                    </div>

                    <Nav className="flex-column gap-2">
                        {/* Mobile Nav Links */}
                        <div className="d-lg-none">
                            <LinkContainer to="/" onClick={handleClose}>
                                <Nav.Link className="text-walnut fw-bold fs-5">Home</Nav.Link>
                            </LinkContainer>
                            <LinkContainer to="/shop" onClick={handleClose}>
                                <Nav.Link className="text-walnut fw-bold fs-5">Shop</Nav.Link>
                            </LinkContainer>
                            <LinkContainer to="/location" onClick={handleClose}>
                                <Nav.Link className="text-walnut fw-bold fs-5">Location</Nav.Link>
                            </LinkContainer>
                            <LinkContainer to="/blog" onClick={handleClose}>
                                <Nav.Link className="text-walnut fw-bold fs-5">Blogs</Nav.Link>
                            </LinkContainer>
                            <hr />
                        </div>

                        {/* Publish Business Option */}
                        <LinkContainer to={{ pathname: '/location', search: '?publish=true' }} onClick={handleClose}>
                            <Nav.Link className="text-walnut fw-bold fs-5">
                                Share Your Spot
                            </Nav.Link>
                        </LinkContainer>

                        {/* Dark Mode Toggle */}
                        <div className="d-flex align-items-center justify-content-between py-2">
                            <span className="text-walnut fw-bold fs-5">Dark Mode</span>
                            <Button variant="link" onClick={toggleTheme} className="text-deep-green p-0">
                                {theme === 'light' ? <FaMoon size={24} /> : <FaSun size={24} />}
                            </Button>
                        </div>

                        {userInfo && (
                            <>
                                <hr />
                                <LinkContainer to={`/profile/${userInfo._id}`} onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Profile</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/profile" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Edit Profile</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/wishlist" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Wishlist</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/message-center" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Messages</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/seller/productlist" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">My Products</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/myspots" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">My Spots</Nav.Link>
                                </LinkContainer>
                            </>
                        )}

                        {userInfo && userInfo.isAdmin && (
                            <>
                                <hr />
                                <h6 className="text-gold fw-bold mt-2">Admin</h6>
                                <LinkContainer to="/admin/userlist" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Users</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/admin/productlist" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Products</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/admin/postlist" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Blog Posts</Nav.Link>
                                </LinkContainer>
                                <LinkContainer to="/admin/bannerlist" onClick={handleClose}>
                                    <Nav.Link className="text-walnut mb-1">Banners</Nav.Link>
                                </LinkContainer>
                            </>
                        )}

                        {userInfo && (
                            <>
                                <hr />
                                <Nav.Link onClick={logoutHandler} className="text-danger fw-bold mt-2">
                                    Logout
                                </Nav.Link>
                            </>
                        )}

                        {!userInfo && (
                            <LinkContainer to="/login" onClick={handleClose}>
                                <Nav.Link className="text-walnut fw-bold fs-5">Login</Nav.Link>
                            </LinkContainer>
                        )}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </header >
    );
};

export default Header;